import React from 'react';

import { UiComponentConfig } from '@bcp-nextgen-dx-touchpoint-framework/react-js';
import {
	TFComponent,
	TFTemplateDisplayGroup,
	TFTemplateDisplayGroupOptions,
	TFTemplateModelComponentOptions,
	TFTemplatePlaceholder,
	TFTemplatePlaceholderOptions,
} from '@types';
import { displayGroups, placeholders } from '@ui';

interface CreateComponentMethodInput<TName extends string> {
	component: React.ComponentType;
	name: TName;
}

let componentId = 0;

export class TouchPointHelper {
	static createDisplayGroup = (
		item: TFComponent,
		containedDisplayGroups?: TFTemplateDisplayGroup[],
		placeholdersTemplate?: TFTemplatePlaceholder[],
		options?: TFTemplateDisplayGroupOptions
	): TFTemplateDisplayGroup => {
		const {
			displayGroupIsInitiallyFocused = true,
			displayGroupBuildOrder = 1,
			displayGroupIsIterable = false,
			displayGroupIsScrollable = false,
			displayGroupIsSwipeable = true,
			displayGroupContainsDisplayGroups = true,
		} = options || {};

		return {
			displayGroupId: item.id,
			displayGroupName: item.name,
			displayGroupIsInitiallyFocused,
			displayGroupBuildOrder,
			displayGroupIsIterable,
			displayGroupIsScrollable,
			displayGroupIsSwipeable,
			displayGroupContainsDisplayGroups,
			placeholders: placeholdersTemplate || [],
			containedDisplayGroups: containedDisplayGroups || [],
		};
	};

	static createPlaceholder = (item: TFComponent, options?: TFTemplatePlaceholderOptions): TFTemplatePlaceholder => {
		const { placeholderBuildReference = 1 } = options || {};

		return {
			defaultUiComponentId: item.id,
			placeholderBuildReference,
		};
	};

	static createUiModelComponent = (
		item: TFComponent,
		options?: TFTemplateModelComponentOptions
	): UiComponentConfig => {
		const {
			uiComponentDescription = item.name,
			uiComponentInfiniteScrolling = false,
			uiComponentIsIterable = false,
			uiComponentIterationsNavigationDirection = 'Vertical',
			uiComponentMaxInternalInterations = 1,
			uiDesignComponent = ['Design 1'],
		} = options || {};

		return {
			uiComponentId: item.id,
			uiComponentName: item.name,
			uiComponentDescription,
			uiComponentIterationsNavigationDirection,
			uiComponentMaxInternalInterations,
			uiComponentIsIterable,
			uiComponentInfiniteScrolling,
			uiDesignComponent,
		};
	};

	static getUiModelPlaceholders = (): UiComponentConfig[] => {
		return Object.values(placeholders).map((x) => TouchPointHelper.createUiModelComponent(x));
	};

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	static getUiElementsByName = (): any => {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const displayGroupsTransform: Record<string, React.ComponentType<any>> = {};
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const componentsTransform: Record<string, React.ComponentType<any>> = {};

		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		Object.values(displayGroups).forEach((x: any) => {
			displayGroupsTransform[x.name] = x.component;
		});

		Object.values(placeholders).forEach((x) => {
			componentsTransform[x.name] = x.component;
		});

		return {
			displayGroups: displayGroupsTransform,
			components: componentsTransform,
		};
	};

	static createComponent = <TName extends string>({
		component,
		name,
	}: CreateComponentMethodInput<TName>): TFComponent => {
		componentId++;

		return {
			component,
			id: componentId,
			name: name,
		};
	};

	static createComponentsGroup = <TName extends string>(
		components: CreateComponentMethodInput<TName>[]
	): Record<TName, TFComponent> => {
		const componentsGroup: Record<string, TFComponent> = {};

		components.forEach((x) => {
			componentsGroup[x.name] = TouchPointHelper.createComponent(x);
		});

		return componentsGroup;
	};
}
